import React, { useState, useRef, useEffect, useCallback } from "react";
import { X } from "lucide-react";
import { useRedditForm } from "../../../../../hooks/useRedditForm";
import { PlatformFormProps } from "./types";

const DEBOUNCE_MS = 300;

const RedditForm: React.FC<PlatformFormProps> = ({
  postTitle,
  setPostTitle,
  postBody,
  setPostBody,
  mediaPreviewUrls,
  mediaMetadata,
  removeMedia,
  fileInputRef,
  draggedIndex,
  handleImageDragStart,
  handleImageDragOver,
  handleImageDrop,
  handleImageDragEnd,
  handleFileChange,
  handleDragOver,
  handleDrop,
  subreddits = [],
  setSubreddits,
  searchSubreddit,
  useCommonMediaForAllPlatforms,
  setUseCommonMediaForAllPlatforms,
  showCommonMediaToggle,
}) => {
  const { validation, requirements: REQUIREMENTS } =
    useRedditForm(mediaMetadata);
  const maxImages =
    typeof REQUIREMENTS.maxImages === "number" ? REQUIREMENTS.maxImages : 1;

  const [subredditQuery, setSubredditQuery] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doSearch = useCallback(
    async (query: string) => {
      if (!query.trim() || !searchSubreddit) {
        setSuggestions([]);
        return;
      }
      setSearching(true);
      try {
        const list = await searchSubreddit(query.trim());
        setSuggestions(list ?? []);
        setSuggestionsOpen(true);
      } finally {
        setSearching(false);
      }
    },
    [searchSubreddit]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch(subredditQuery);
      debounceRef.current = null;
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [subredditQuery, doSearch]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setSuggestionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addSubreddit = (name: string) => {
    const normalized = name.startsWith("r/") ? name : `r/${name}`;
    if (setSubreddits) {
      setSubreddits([normalized]);
    }
    setSubredditQuery("");
    setSuggestions([]);
    setSuggestionsOpen(false);
  };

  const removeSubreddit = (name: string) => {
    if (setSubreddits) {
      setSubreddits(subreddits.filter((s) => s !== name));
    }
  };

  return (
    <>
      {/* Content Fine-Tuning */}
      <div className="form-section">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "16px",
          }}
        >
          <label className="section-label" style={{ marginBottom: 0 }}>
            Content for Reddit
          </label>
        </div>

        <div className="input-group">
          <label
            className="section-label"
            style={{ fontWeight: 500, color: "#6b7280" }}
          >
            Title <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <input
            type="text"
            className="step3-input"
            value={postTitle}
            onChange={(e) => setPostTitle(e.target.value)}
            placeholder="Enter post title..."
            required
          />
        </div>

        <div className="input-group">
          <label
            className="section-label"
            style={{ fontWeight: 500, color: "#6b7280" }}
          >
            Post Body Content <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <textarea
            className="step3-textarea"
            value={postBody}
            onChange={(e) => setPostBody(e.target.value)}
            placeholder="Write your post content here..."
            required
          />
        </div>

        {/* Subreddits - required when Reddit is selected */}
        {searchSubreddit && (
          <div className="input-group" ref={dropdownRef} style={{ position: "relative" }}>
            <label
              className="section-label"
              style={{ fontWeight: 500, color: "#6b7280" }}
            >
              Subreddit <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="text"
              className="step3-input"
              value={subredditQuery}
              onChange={(e) => setSubredditQuery(e.target.value)}
              onFocus={() => suggestions.length > 0 && setSuggestionsOpen(true)}
              placeholder="Search and select a subreddit..."
              disabled={subreddits.length > 0}
            />
            {suggestionsOpen && (suggestions.length > 0 || searching) && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  marginTop: "4px",
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  maxHeight: "200px",
                  overflowY: "auto",
                  zIndex: 10,
                }}
              >
                {searching ? (
                  <div style={{ padding: "12px 14px", color: "#6b7280", fontSize: "13px" }}>
                    Searching...
                  </div>
                ) : (
                  suggestions.map((name) => {
                    const display = name.startsWith("r/") ? name : `r/${name}`;
                    const isSelected = subreddits.length > 0 && subreddits[0] === display;
                    return (
                      <button
                        key={display}
                        type="button"
                        onClick={() => !isSelected && addSubreddit(name)}
                        disabled={isSelected}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          textAlign: "left",
                          border: "none",
                          background: isSelected ? "#f9fafb" : "transparent",
                          color: isSelected ? "#9ca3af" : "#1f2937",
                          fontSize: "14px",
                          cursor: isSelected ? "default" : "pointer",
                        }}
                      >
                        {display}
                        {isSelected && " (selected)"}
                      </button>
                    );
                  })
                )}
              </div>
            )}
            {subreddits.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                  marginTop: "8px",
                }}
              >
                {subreddits.map((name) => (
                  <span
                    key={name}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "4px 8px",
                      background: "#ff4500",
                      color: "#fff",
                      borderRadius: "999px",
                      fontSize: "12px",
                      fontWeight: 500,
                    }}
                  >
                    {name}
                    <button
                      type="button"
                      onClick={() => removeSubreddit(name)}
                      style={{
                        display: "flex",
                        padding: 0,
                        border: "none",
                        background: "none",
                        color: "inherit",
                        cursor: "pointer",
                      }}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {showCommonMediaToggle && (
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              padding: "14px 16px",
              marginTop: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#1f2937",
                  }}
                >
                  Common media for all platforms
                </span>
                <p
                  style={{
                    margin: "6px 0 0",
                    fontSize: "12px",
                    color: "#6b7280",
                    lineHeight: "1.4",
                  }}
                >
                  {useCommonMediaForAllPlatforms
                    ? "Use the same uploaded media across all selected platforms."
                    : "Send media in each platform-specific configuration."}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!useCommonMediaForAllPlatforms}
                onClick={() =>
                  setUseCommonMediaForAllPlatforms?.(
                    !useCommonMediaForAllPlatforms
                  )
                }
                style={{
                  width: "40px",
                  height: "22px",
                  borderRadius: "11px",
                  border: "none",
                  background: useCommonMediaForAllPlatforms
                    ? "#4f46e5"
                    : "#d1d5db",
                  position: "relative",
                  cursor: "pointer",
                  transition: "background 0.2s",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    background: "#fff",
                    position: "absolute",
                    top: "3px",
                    left: useCommonMediaForAllPlatforms ? "21px" : "3px",
                    transition: "left 0.2s",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                  }}
                />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Media Assets */}
      <div className="form-section">
        <label className="section-label">Media Assets</label>

        {/* Platform Requirements Info */}
        <div className="platform-requirements">
          <div
            className="req-row"
            style={{
              background:
                validation.imageCountValid === true
                  ? "#f0fdf4"
                  : validation.imageCountValid === false
                  ? "#fef2f2"
                  : "transparent",
            }}
          >
            <span className="req-label">
              {validation.imageCountValid === true && (
                <span style={{ color: "#16a34a", marginRight: "6px" }}>✓</span>
              )}
              {validation.imageCountValid === false && (
                <span style={{ color: "#dc2626", marginRight: "6px" }}>✗</span>
              )}
              Images:
            </span>
            <span className="req-value">
              Up to {REQUIREMENTS.maxImages} • {REQUIREMENTS.imageFormats}
            </span>
          </div>
          <div
            className="req-row"
            style={{
              background:
                validation.imageSizeValid === true
                  ? "#f0fdf4"
                  : validation.imageSizeValid === false
                  ? "#fef2f2"
                  : "transparent",
            }}
          >
            <span className="req-label">
              {validation.imageSizeValid === true && (
                <span style={{ color: "#16a34a", marginRight: "6px" }}>✓</span>
              )}
              {validation.imageSizeValid === false && (
                <span style={{ color: "#dc2626", marginRight: "6px" }}>✗</span>
              )}
              Image Size:
            </span>
            <span className="req-value">Max {REQUIREMENTS.maxImageSize}</span>
          </div>
          <div className="req-row">
            <span className="req-label">Dimensions:</span>
            <span className="req-value">
              Min: {REQUIREMENTS.minDimensions} • Max:{" "}
              {REQUIREMENTS.maxDimensions}
            </span>
          </div>
          <div className="req-row">
            <span className="req-label">Aspect Ratios:</span>
            <span className="req-value">{REQUIREMENTS.aspectRatios}</span>
          </div>
          <div className="req-row">
            <span className="req-label">Recommended:</span>
            <span className="req-value">{REQUIREMENTS.recommended}</span>
          </div>
          {REQUIREMENTS.note && (
            <div className="req-note">{REQUIREMENTS.note}</div>
          )}
        </div>

        {/* Warning for excess images */}
        {mediaPreviewUrls.length > maxImages && (
          <div
            style={{
              background: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "8px",
              padding: "10px 12px",
              marginBottom: "12px",
              fontSize: "13px",
              color: "#856404",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span style={{ fontSize: "16px" }}>⚠️</span>
            <span>
              Only {maxImages} {maxImages === 1 ? "image" : "images"} will be
              taken for Reddit. The first {maxImages} will be used.
            </span>
          </div>
        )}

        <div
          className="media-upload-area"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => {
            if (mediaPreviewUrls.length < maxImages) {
              fileInputRef.current?.click();
            }
          }}
          style={{
            cursor:
              mediaPreviewUrls.length >= maxImages ? "not-allowed" : "pointer",
            position: "relative",
            overflow: "hidden",
            padding: mediaPreviewUrls.length > 0 ? "10px" : "20px",
            display: "flex",
            flexDirection: mediaPreviewUrls.length > 0 ? "row" : "column",
            flexWrap: "wrap",
            gap: "10px",
            alignItems: "center",
            justifyContent:
              mediaPreviewUrls.length > 0 ? "flex-start" : "center",
          }}
        >
          {mediaPreviewUrls.length > 0 ? (
            <>
              {mediaPreviewUrls.map((url, index) => {
                const isDisabled = index >= maxImages;
                const media = mediaMetadata[index];
                const isVideo = media?.type === "video";

                return (
                  <div
                    key={url}
                    draggable
                    onDragStart={(e) => handleImageDragStart(e, index)}
                    onDragOver={handleImageDragOver}
                    onDrop={(e) => handleImageDrop(e, index)}
                    onDragEnd={handleImageDragEnd}
                    style={{
                      position: "relative",
                      width: "80px",
                      height: "80px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      flexShrink: 0,
                      cursor: "move",
                      opacity: isDisabled
                        ? 0.4
                        : draggedIndex === index
                        ? 0.5
                        : 1,
                      border: isDisabled
                        ? "2px solid #ef4444"
                        : draggedIndex === index
                        ? "2px solid #4f46e5"
                        : "2px solid transparent",
                      filter: isDisabled ? "grayscale(100%)" : "none",
                    }}
                  >
                    {isDisabled && (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: "rgba(239, 68, 68, 0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 1,
                        }}
                      >
                        <span
                          style={{
                            background: "rgba(239, 68, 68, 0.9)",
                            color: "white",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontSize: "10px",
                            fontWeight: "600",
                          }}
                        >
                          DISABLED
                        </span>
                      </div>
                    )}

                    {isVideo ? (
                      <>
                        <video
                          src={url}
                          preload="metadata"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            pointerEvents: "none",
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            background: "rgba(0, 0, 0, 0.6)",
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pointerEvents: "none",
                          }}
                        >
                          <div
                            style={{
                              width: 0,
                              height: 0,
                              borderLeft: "6px solid white",
                              borderTop: "4px solid transparent",
                              borderBottom: "4px solid transparent",
                              marginLeft: "2px",
                            }}
                          />
                        </div>
                        {media?.duration && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: 4,
                              right: 4,
                              background: "rgba(0, 0, 0, 0.7)",
                              color: "white",
                              padding: "2px 4px",
                              borderRadius: "3px",
                              fontSize: "9px",
                              fontWeight: "600",
                            }}
                          >
                            {Math.floor(media.duration / 60)}:
                            {String(Math.floor(media.duration % 60)).padStart(
                              2,
                              "0"
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <img
                        src={url}
                        alt={`Uploaded ${index}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          pointerEvents: "none",
                        }}
                      />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeMedia(index);
                      }}
                      style={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        background: "rgba(0,0,0,0.6)",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        zIndex: 2,
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              {mediaPreviewUrls.length < maxImages && (
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    border: "2px dashed #ccc",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ccc",
                    fontSize: "24px",
                    flexShrink: 0,
                  }}
                >
                  +
                </div>
              )}
            </>
          ) : (
            <>
              <div className="upload-icon">☁️</div>
              <span className="upload-text">Upload or drag media</span>
            </>
          )}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept="image/*"
            onChange={handleFileChange}
          />
        </div>
        {mediaPreviewUrls.length > 0 && (
          <div
            style={{
              fontSize: "12px",
              color:
                mediaPreviewUrls.length >= maxImages ? "#ef4444" : "#6b7280",
              marginTop: "4px",
              fontWeight: mediaPreviewUrls.length >= maxImages ? 500 : 400,
            }}
          >
            {mediaPreviewUrls.length} / {maxImages} file selected
            {mediaPreviewUrls.length >= maxImages && " (Maximum reached)"}
          </div>
        )}
      </div>
    </>
  );
};

export default RedditForm;
